import type { Agency } from '@saaga/shared/interfaces/agencies.interface'
import type { AgencyResponse } from '@saaga/shared/interfaces/agency.interface'
import { useGetAgencyAnyQuery } from '@saaga/shared/store/api.store'
import { useCallback, useMemo } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { useOrgSlug } from '@/hooks/use-org-slug.hook'
import { orgRoutes } from '@/routes/org-routes.const'

import { mapAgencyToAgency } from './agency-response-mapper.util'
import { getTabFromPath } from './agency-tab.util'

export interface UseAgencyDetailReturn {
  agency: Agency | undefined
  agencyResponse: AgencyResponse | undefined
  isLoading: boolean
  isError: boolean
  handleBack: () => void
  handleStartChat: (agency: Agency) => void
}

export const useAgencyDetail = (): UseAgencyDetailReturn => {
  const navigate = useNavigate()
  const location = useLocation()
  const orgSlug = useOrgSlug()
  const { agencySlug } = useParams<{ agencySlug: string }>()

  const currentTab = getTabFromPath(location.pathname)

  const {
    data: agencyResponse,
    isLoading,
    isError,
  } = useGetAgencyAnyQuery(agencySlug ?? '', {
    skip: !agencySlug,
  })

  const agency = useMemo(() => {
    if (!agencyResponse) {
      return undefined
    }
    return mapAgencyToAgency(agencyResponse)
  }, [agencyResponse])

  const handleBack = useCallback((): void => {
    navigate(`/${orgSlug}/${orgRoutes.agencies}/${currentTab}`)
  }, [navigate, orgSlug, currentTab])

  const handleStartChat = useCallback(
    (agency: Agency): void => {
      navigate(`/${orgSlug}/${orgRoutes.chat}/${agency.slug}`)
    },
    [navigate, orgSlug]
  )

  return {
    agency,
    agencyResponse,
    isLoading,
    isError,
    handleBack,
    handleStartChat,
  }
}
